/**
 * ComposePage — Compose surface for a single recipient profile (B22).
 *
 * Operationalizes PeoplePrints' core category claim: "psychological prep for
 * the moment of contact." The Blueprint reads the person; this surface does
 * the prep for the actual email the manager will send. UI copy holds that
 * framing throughout — "Draft tuned to how Jane tends to receive feedback,"
 * NOT "Personalized using Jane's profile."
 *
 * Surface = modal/inline panel rendered from inside ProfileDetail when the
 * user selects the Compose tab. Three phases:
 *   1. Picker — choose one of 6 scenarios + optional context
 *   2. Streaming — progress card while the draft generates
 *   3. Result  — subject + body + rationale + chat-style refinement loop +
 *                "Open in email" (mailto:) + "Copy to clipboard" fallback
 *
 * Drafts persist in `email_drafts`; recent drafts render as a list on this
 * page so the manager can re-open one without regenerating.
 */

import { useState, useEffect, useCallback } from "react";
import { Callout, Chip, ChatTurn, Composer, SectionHeader } from "../components/index.js";
import StreamingMarkdown from "../components/StreamingMarkdown.jsx";
import { parseJsonResponse } from "../lib/api-fetch.js";
import { terminalActionButtonStyle, terminalActionButtonDisabledStyle } from "../lib/buttonStyles.js";

// ──────────────────────────────────────────────────────────────────────────
// Scenario pick-list — locked V1 set of 6 (B22 build prompt). Labels hold the
// "psychological prep" framing; descriptions explain the moment of contact
// each scenario serves. No additional scenarios in V1 — queue post-launch.
// ──────────────────────────────────────────────────────────────────────────

export const COMPOSE_SCENARIOS = [
  {
    value: "coaching_note",
    label: "Re-engage / course-correct",
    headline: "The note that gets a slipping deal or relationship back on track",
    description: "They've gone quiet, momentum has stalled, or something's drifting off course. This note names it without pressure — hedged, forward-framed, tuned to how this person takes directness.",
  },
  {
    value: "recognition",
    label: "Congratulate a win",
    headline: "Praise tuned to public-vs-private preference",
    description: "They closed something, launched something, got promoted. Specific recognition that matches how this person likes to receive it — public spotlight, private direct, or private-then-delayed-public.",
  },
  {
    value: "one_on_one_prep",
    label: "Pre-meeting note",
    headline: "A short heads-up before the meeting",
    description: "Brief message ahead of your next conversation — tuned to whether this person prefers an agenda in advance or walks in cold.",
  },
  {
    value: "difficult_conversation_heads_up",
    label: "Hard conversation — heads-up",
    headline: "The framing message BEFORE a hard conversation",
    description: "Soft warning shot before a tough talk — a renegotiation, a pushback, a no. The same hard news lands four very different ways depending on the person; this draft picks the framing that tends to work for them.",
  },
  {
    value: "difficult_news_cover",
    label: "Hard news — personal cover",
    headline: "Your personal note alongside a formal announcement",
    description: "The human note that goes with a price change, a delay, a scope cut, or a policy shift. Distinct from the formal notice — this is your voice, counterpart to counterpart.",
  },
  {
    value: "welcome_onboarding",
    label: "First outreach / welcome",
    headline: "The first message that sets the relationship's tone",
    description: "A new client, a fresh prospect, a just-signed partner. Specific, warm, and tuned to how this person tends to size people up.",
  },
];

// ──────────────────────────────────────────────────────────────────────────
// Tone-shift quick chips for the refinement loop. Each chip is a one-click
// refinement that prepends a structured instruction the manager would
// otherwise type out. The chat composer accepts arbitrary text alongside.
// ──────────────────────────────────────────────────────────────────────────

const REFINE_CHIPS = [
  { label: "Warmer", text: "Make the tone warmer without losing the substance." },
  { label: "More direct", text: "Make the tone more direct. Cut throat-clearing." },
  { label: "Shorter", text: "Shorten the body. Keep the core point." },
  { label: "Add specifics", text: "The body is too abstract. Reference one specific thing from the additional context above." },
  { label: "Different opening", text: "Rewrite the opening line. The current one isn't landing." },
  { label: "Different close", text: "Rewrite the close. Keep the same next step but change the framing." },
];

// ──────────────────────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────────────────────

export default function ComposePage({ profile, profileId, session, onClose }) {
  const [phase, setPhase] = useState("picker");        // picker | streaming | result
  const [scenario, setScenario] = useState(null);
  const [additionalContext, setAdditionalContext] = useState("");
  const [draftId, setDraftId] = useState(null);
  const [currentDraft, setCurrentDraft] = useState(null); // { subject, body, rationale, hedge_phrases, engine_version, turn_index }
  const [refinementHistory, setRefinementHistory] = useState([]); // chat turns: { role, content }
  const [streamRequest, setStreamRequest] = useState(null);
  const [recentDrafts, setRecentDrafts] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copyState, setCopyState] = useState(null); // null | "copied"

  // ── Load recent drafts for this profile ─────────────────────────────────
  const loadRecent = useCallback(async () => {
    if (!session?.access_token || !profileId) return;
    setRecentLoading(true);
    try {
      const res = await fetch(`/api/compose-drafts/list?profile_id=${encodeURIComponent(profileId)}&limit=5`, {
        headers: { authorization: `Bearer ${session.access_token}` },
      });
      const body = await parseJsonResponse(res);
      if (res.ok) setRecentDrafts(body.drafts || []);
    } catch (e) {
      // Recent-list failures are non-blocking; the picker still works.
      console.warn("compose-drafts list failed:", e?.message);
    } finally {
      setRecentLoading(false);
    }
  }, [session?.access_token, profileId]);
  useEffect(() => { loadRecent(); }, [loadRecent]);

  // ── Phase transitions ───────────────────────────────────────────────────
  const startGenerate = () => {
    if (!scenario) {
      setError("Pick a scenario first.");
      return;
    }
    setError(null);
    setRefinementHistory([]);
    setCurrentDraft(null);
    setDraftId(null);
    setStreamRequest({
      profile_id: profileId,
      scenario,
      additional_context: additionalContext.trim() || null,
      draft_id: null,
      refinement_instruction: null,
    });
    setPhase("streaming");
  };

  const handleStreamDone = (payload) => {
    setDraftId(payload.draft_id);
    setCurrentDraft({
      subject: payload.subject,
      body: payload.body,
      rationale: payload.rationale,
      hedge_phrases: payload.hedge_phrases || [],
      engine_version: payload.engine_version,
      turn_index: payload.turn_index,
    });
    setStreamRequest(null);
    setPhase("result");
    loadRecent(); // refresh the recent-drafts list so the new draft shows up
  };

  // ── Refinement turn ─────────────────────────────────────────────────────
  const refine = (instruction) => {
    if (!instruction?.trim() || !draftId || !currentDraft) return;
    setError(null);
    // Optimistic chat-turn render so the user sees their instruction land
    // before the new draft streams in.
    setRefinementHistory((h) => [
      ...h,
      { role: "user", content: instruction },
    ]);
    setStreamRequest({
      profile_id: profileId,
      scenario,
      additional_context: additionalContext.trim() || null,
      draft_id: draftId,
      refinement_instruction: instruction,
    });
    setPhase("streaming");
  };

  const handleRefineDone = (payload) => {
    setCurrentDraft({
      subject: payload.subject,
      body: payload.body,
      rationale: payload.rationale,
      hedge_phrases: payload.hedge_phrases || [],
      engine_version: payload.engine_version,
      turn_index: payload.turn_index,
    });
    // Append an assistant turn confirming the refinement landed; the
    // updated draft itself renders below.
    setRefinementHistory((h) => [
      ...h,
      { role: "assistant", content: "Updated. The refined draft is above — review the changes." },
    ]);
    setStreamRequest(null);
    setPhase("result");
    loadRecent();
  };

  // Unified onDone — dispatches to generate or refine based on whether
  // we have a draftId pre-stream. Simplifies the StreamingMarkdown wiring.
  const handleDone = (payload) => {
    if (draftId && refinementHistory.length > 0 && refinementHistory[refinementHistory.length - 1].role === "user") {
      handleRefineDone(payload);
    } else {
      handleStreamDone(payload);
    }
  };

  // ── Re-open a saved draft ───────────────────────────────────────────────
  const openSaved = async (savedId) => {
    if (!session?.access_token) return;
    setError(null);
    try {
      const res = await fetch(`/api/compose-drafts/get?draft_id=${encodeURIComponent(savedId)}`, {
        headers: { authorization: `Bearer ${session.access_token}` },
      });
      const body = await parseJsonResponse(res);
      if (!res.ok) {
        setError(body.error || "Failed to load draft");
        return;
      }
      setDraftId(body.id);
      setScenario(body.scenario);
      setAdditionalContext(body.additional_context || "");
      setCurrentDraft({
        subject: body.subject,
        body: body.body,
        rationale: body.rationale,
        hedge_phrases: body.hedge_phrases || [],
        engine_version: body.engine_version,
        turn_index: (body.refinement_history || []).length - 1,
      });
      // Replay the refinement history into the chat-turn ledger so the
      // re-opened draft shows its prior arc.
      const chatTurns = [];
      for (const t of body.refinement_history || []) {
        if (t.user_instruction) {
          chatTurns.push({ role: "user", content: t.user_instruction });
          chatTurns.push({ role: "assistant", content: "Updated." });
        }
      }
      setRefinementHistory(chatTurns);
      setPhase("result");
    } catch (e) {
      setError(e.message);
    }
  };

  // ── Open in email (mailto:) with fallback to clipboard ──────────────────
  const MAILTO_LENGTH_CEILING = 2000; // practical limit; real ceilings vary by client + OS
  const openInEmail = () => {
    if (!currentDraft) return;
    const url = buildMailtoUrl(currentDraft.subject, currentDraft.body);
    if (url.length > MAILTO_LENGTH_CEILING) {
      // Fall back to copy-to-clipboard with a toast telling the user to
      // paste into their mail client. Per the B22 build prompt: long
      // drafts exceed the practical mailto: URL ceiling and silently
      // fail in some mail clients — the copy fallback is the
      // V1-shipped graceful degradation.
      copyToClipboard();
      setError("Draft is long — copied to clipboard instead. Paste into your email client.");
      return;
    }
    window.location.href = url;
  };

  const copyToClipboard = async () => {
    if (!currentDraft) return;
    const text = `Subject: ${currentDraft.subject}\n\n${currentDraft.body}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopyState("copied");
      setTimeout(() => setCopyState(null), 2500);
    } catch {
      setError("Could not access clipboard. Select the draft above and copy manually.");
    }
  };

  // ──────────────────────────────────────────────────────────────────────
  return (
    <div data-component="ComposePage" style={{ marginTop: 16 }}>
      {/* Buyer-facing framing eyebrow — holds the "psychological prep for the
          moment of contact" category claim. NOT "AI-drafted emails." */}
      <div
        style={{
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: 11,
          letterSpacing: "0.10em",
          textTransform: "uppercase",
          color: "var(--ppr-color-accent-coral)",
          marginBottom: 6,
        }}
        data-testid="compose-eyebrow"
      >
        Compose — psychological prep for the moment of contact
      </div>
      <SectionHeader index="—" title={`Draft a message to ${profile?.display_name || "your recipient"}`} />
      <p style={{
        fontFamily: "var(--ppr-typography-font-family-body)",
        fontSize: "var(--ppr-typography-font-size-base)",
        color: "var(--ppr-color-text-secondary)",
        lineHeight: 1.55,
        marginBottom: 24,
      }}>
        The Blueprint reads the person. This surface does the prep work for the
        actual conversation. Pick a scenario and we'll draft a starting note
        tuned to how {profile?.display_name || "this recipient"} tends to
        receive that kind of message.
      </p>

      {error && (
        <div style={{ marginBottom: 16 }}>
          <Callout.WatchOut>{error}</Callout.WatchOut>
        </div>
      )}

      {/* PHASE 1 — Scenario picker (also shown alongside Recent drafts when
          there's nothing in-flight). */}
      {phase === "picker" && (
        <ScenarioPicker
          scenario={scenario}
          setScenario={setScenario}
          additionalContext={additionalContext}
          setAdditionalContext={setAdditionalContext}
          onStart={startGenerate}
        />
      )}

      {/* PHASE 2 — Streaming progress card. The Compose surface intentionally
          mirrors the Blueprint streaming progress posture: progress card while
          the draft generates; the body renders only after `done`. */}
      {phase === "streaming" && streamRequest && (
        <StreamingMarkdown
          url="/api/compose-email/stream"
          bearerToken={session?.access_token}
          body={streamRequest}
          onDone={handleDone}
          onRetry={() => {
            const r = streamRequest;
            setStreamRequest(null);
            setTimeout(() => setStreamRequest(r), 0);
          }}
          surfaceLabel="Compose draft"
          progressLine="Writing the note — tuning subject and body to how they receive."
        />
      )}

      {/* PHASE 3 — Result: subject + body + rationale + refinement loop +
          Open in email / Copy. */}
      {phase === "result" && currentDraft && (
        <DraftResult
          draft={currentDraft}
          scenario={scenario}
          recipientName={profile?.display_name}
          refinementHistory={refinementHistory}
          onRefine={refine}
          onOpenInEmail={openInEmail}
          onCopy={copyToClipboard}
          copyState={copyState}
          onStartOver={() => {
            setPhase("picker");
            setCurrentDraft(null);
            setDraftId(null);
            setScenario(null);
            setAdditionalContext("");
            setRefinementHistory([]);
            setCopyState(null);
            setError(null);
          }}
        />
      )}

      {/* Recent drafts — shown on the picker phase, and on result phase too
          so the manager can switch between drafts without losing context. */}
      {(phase === "picker" || phase === "result") && (
        <RecentDraftsPanel
          drafts={recentDrafts}
          loading={recentLoading}
          activeId={draftId}
          onOpen={openSaved}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────────────────

function ScenarioPicker({ scenario, setScenario, additionalContext, setAdditionalContext, onStart }) {
  return (
    <div data-component="ScenarioPicker">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14, marginBottom: 24 }}>
        {COMPOSE_SCENARIOS.map((s) => (
          <button
            key={s.value}
            type="button"
            onClick={(e) => { setScenario(s.value); e.currentTarget.blur(); }}
            data-testid={`scenario-${s.value}`}
            style={{
              ...scenarioCardStyle,
              ...(scenario === s.value ? scenarioCardSelectedStyle : null),
            }}
          >
            <div style={scenarioLabelStyle}>{s.label}</div>
            <div style={scenarioHeadlineStyle}>{s.headline}</div>
            <div style={scenarioDescStyle}>{s.description}</div>
          </button>
        ))}
      </div>

      <label style={{
        display: "block",
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: 11,
        letterSpacing: "0.10em",
        textTransform: "uppercase",
        color: "var(--ppr-color-text-muted)",
        marginBottom: 6,
      }}>
        Optional context
      </label>
      <textarea
        value={additionalContext}
        onChange={(e) => setAdditionalContext(e.target.value)}
        data-testid="compose-context"
        placeholder="Anything specific that prompted this — a recent project, a comment, a deadline. The draft uses this to land specifically rather than generically. Leave blank if there's nothing to add."
        rows={4}
        style={textareaStyle}
      />

      <div style={{ marginTop: 16 }}>
        <button
          type="button"
          onClick={onStart}
          disabled={!scenario}
          data-testid="compose-generate"
          style={scenario ? terminalActionButtonStyle : terminalActionButtonDisabledStyle}
        >
          Draft this message
        </button>
      </div>
    </div>
  );
}

function DraftResult({ draft, scenario, recipientName, refinementHistory, onRefine, onOpenInEmail, onCopy, copyState, onStartOver }) {
  const scenarioMeta = COMPOSE_SCENARIOS.find((s) => s.value === scenario);
  return (
    <div data-component="DraftResult">
      <div style={draftCardStyle}>
        <div style={draftEyebrowStyle}>
          {scenarioMeta?.label || scenario}{recipientName ? ` · to ${recipientName}` : ""}
        </div>
        <div style={subjectRowStyle}>
          <div style={subjectLabelStyle}>Subject</div>
          <div style={subjectValueStyle} data-testid="draft-subject">{draft.subject}</div>
        </div>
        <pre style={bodyStyle} data-testid="draft-body">{draft.body}</pre>
      </div>

      {draft.rationale && (
        <div style={{ marginTop: 16 }}>
          <Callout.KeyInsight>
            <strong>Why this draft was tuned this way.</strong>{" "}
            <span data-testid="draft-rationale">{draft.rationale}</span>
          </Callout.KeyInsight>
        </div>
      )}

      <div style={{ display: "flex", gap: 12, marginTop: 20, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={onOpenInEmail}
          data-testid="compose-open-email"
          style={terminalActionButtonStyle}
        >
          Open in email
        </button>
        <button
          type="button"
          onClick={onCopy}
          data-testid="compose-copy"
          style={ghostButtonStyle}
        >
          {copyState === "copied" ? "Copied" : "Copy to clipboard"}
        </button>
        <button
          type="button"
          onClick={onStartOver}
          style={ghostButtonStyle}
        >
          Start over
        </button>
      </div>

      {/* Refinement chat-layer pattern — chips for common shifts + composer
          for free-text refinements. Each turn re-runs the streaming
          endpoint with draft_id + refinement_instruction, which UPDATES
          the existing email_drafts row and appends to refinement_history. */}
      <div style={{ marginTop: 28 }}>
        <SectionHeader index="—" title="Refine the draft" />
        <div style={{ marginBottom: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
          {REFINE_CHIPS.map((c) => (
            <Chip
              key={c.label}
              onClick={() => onRefine(c.text)}
              data-testid={`refine-chip-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {c.label}
            </Chip>
          ))}
        </div>

        {refinementHistory.length > 0 && (
          <div style={{ marginBottom: 16, display: "flex", flexDirection: "column", gap: 12 }}>
            {refinementHistory.map((turn, idx) => (
              <ChatTurn key={idx} role={turn.role}>
                {turn.content}
              </ChatTurn>
            ))}
          </div>
        )}

        <Composer
          placeholder='Refine the draft — "warmer," "more direct," "rewrite the second paragraph," or anything specific.'
          onSubmit={onRefine}
          data-testid="compose-refine-composer"
        />
      </div>
    </div>
  );
}

function RecentDraftsPanel({ drafts, loading, activeId, onOpen }) {
  if (loading) return null;
  if (!drafts || drafts.length === 0) return null;
  return (
    <div style={{ marginTop: 32 }}>
      <SectionHeader index="—" title="Recent drafts" />
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {drafts.map((d) => {
          const sMeta = COMPOSE_SCENARIOS.find((x) => x.value === d.scenario);
          const isActive = d.id === activeId;
          return (
            <button
              key={d.id}
              type="button"
              onClick={() => onOpen(d.id)}
              data-testid={`recent-draft-${d.id}`}
              style={{
                ...recentRowStyle,
                ...(isActive ? recentRowActiveStyle : null),
              }}
            >
              <div style={{ fontWeight: 600 }}>{d.subject}</div>
              <div style={recentMetaStyle}>
                {sMeta?.label || d.scenario} · {formatRelativeTime(d.updated_at)}
                {d.turn_count > 1 ? ` · ${d.turn_count - 1} refinement${d.turn_count > 2 ? "s" : ""}` : ""}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Pure helpers — exported for unit testing
// ──────────────────────────────────────────────────────────────────────────

/**
 * Build the mailto: URL. Subject + body URL-encoded per RFC 6068. The
 * receiving mail client decodes and pre-fills the compose window.
 *
 * @param {string} subject
 * @param {string} body
 * @returns {string}
 */
export function buildMailtoUrl(subject, body) {
  const s = encodeURIComponent(subject || "");
  const b = encodeURIComponent(body || "");
  return `mailto:?subject=${s}&body=${b}`;
}

/**
 * Coarse "time ago" formatter — minutes / hours / days / months. Used for
 * the recent-drafts list. Avoids a date-fns dependency.
 */
export function formatRelativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.max(0, Math.round((now - then) / 1000));
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 48) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 60) return `${day}d ago`;
  const mo = Math.round(day / 30);
  return `${mo}mo ago`;
}

// ──────────────────────────────────────────────────────────────────────────
// Styles
// ──────────────────────────────────────────────────────────────────────────

const scenarioCardStyle = {
  textAlign: "left",
  padding: 18,
  border: "1px solid var(--ppr-color-border-default)",
  borderRadius: "var(--ppr-radius-sm)",
  background: "var(--ppr-color-background-surface)",
  cursor: "pointer",
  outline: "none",
  fontFamily: "var(--ppr-typography-font-family-body)",
};

// Selected = a clear crimson left rule + warm tint, so exactly one card reads
// as chosen (no ambiguity with a lingering focus ring on a prior click).
const scenarioCardSelectedStyle = {
  border: "1px solid var(--ppr-color-accent-coral)",
  borderLeft: "3px solid var(--ppr-color-accent-coral)",
  background: "var(--ppr-color-background-sub)",
};

const scenarioLabelStyle = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: 11,
  letterSpacing: "0.10em",
  textTransform: "uppercase",
  color: "var(--ppr-color-accent-coral)",
  marginBottom: 6,
};

const scenarioHeadlineStyle = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: 16,
  fontWeight: 700,
  color: "var(--ppr-color-accent-navy)",
  marginBottom: 6,
  lineHeight: 1.3,
};

const scenarioDescStyle = {
  fontSize: 13,
  lineHeight: 1.5,
  color: "var(--ppr-color-text-secondary)",
};

const textareaStyle = {
  width: "100%",
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: 14,
  padding: 12,
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  resize: "vertical",
};

const draftCardStyle = {
  padding: "22px 24px",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  marginTop: 8,
};

const draftEyebrowStyle = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: 11,
  letterSpacing: "0.10em",
  textTransform: "uppercase",
  color: "var(--ppr-color-text-muted)",
  marginBottom: 14,
};

const subjectRowStyle = {
  display: "flex",
  alignItems: "baseline",
  gap: 12,
  paddingBottom: 12,
  marginBottom: 14,
  borderBottom: "1px solid var(--ppr-color-border-default)",
};

const subjectLabelStyle = {
  fontFamily: "var(--ppr-typography-font-family-mono)",
  fontSize: 11,
  textTransform: "uppercase",
  color: "var(--ppr-color-text-muted)",
  letterSpacing: "0.08em",
};

const subjectValueStyle = {
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: 16,
  fontWeight: 600,
  color: "var(--ppr-color-accent-navy)",
};

const bodyStyle = {
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: 15,
  lineHeight: 1.6,
  color: "var(--ppr-color-text-primary)",
  whiteSpace: "pre-wrap",
  wordWrap: "break-word",
  margin: 0,
};

const ghostButtonStyle = {
  padding: "10px 18px",
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: 13,
  letterSpacing: "0.04em",
  background: "transparent",
  border: "1px solid var(--ppr-color-border-default)",
  color: "var(--ppr-color-text-primary)",
  cursor: "pointer",
};

const recentRowStyle = {
  textAlign: "left",
  padding: "12px 14px",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  cursor: "pointer",
  fontFamily: "var(--ppr-typography-font-family-body)",
};

const recentRowActiveStyle = {
  borderColor: "var(--ppr-color-accent-navy)",
};

const recentMetaStyle = {
  fontFamily: "var(--ppr-typography-font-family-mono)",
  fontSize: 11,
  color: "var(--ppr-color-text-muted)",
  marginTop: 4,
};
